import axios from 'axios';

export interface ApiError {
  message: string;
  status: number;
  errors?: Record<string, string[]>;
}

/**
 * Gère les erreurs API et retourne un message cohérent
 * - 422: Erreurs de validation (retourne les erreurs)
 * - Autres: Retourne le message d'erreur ou un message personnalisé
 */
export function handleApiError(error: unknown): ApiError {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status || 500;
    const data = error.response?.data as {
      message?: string;
      errors?: Record<string, string[]>;
    };

    // Erreur de validation (422)
    if (status === 422 && data?.errors) {
      return {
        status,
        message: 'Erreur de validation',
        errors: data.errors,
      };
    }

    // Autres erreurs avec message du serveur
    if (data?.message) {
      return {
        status,
        message: data.message,
      };
    }

    // Erreurs sans message
    const errorMessages: Record<number, string> = {
      400: 'Requête invalide',
      401: 'Authentification requise',
      403: 'Accès refusé',
      404: 'Ressource non trouvée',
      409: 'Conflit avec les données existantes',
      429: 'Trop de requêtes, veuillez réessayer plus tard',
      500: 'Erreur serveur interne',
      503: 'Service indisponible',
    };

    return {
      status,
      message: errorMessages[status] || 'Une erreur est survenue',
    };
  }

  // Erreurs non-Axios
  return {
    status: 500,
    message: error instanceof Error ? error.message : 'Une erreur inconnue s\'est produite',
  };
}

/**
 * Formate les erreurs de validation en message lisible
 */
export function formatValidationErrors(errors: Record<string, string[]>): string {
  return Object.values(errors)
    .flat()
    .join(', ');
}

/**
 * Retourne le message d'erreur pour affichage dans l'UI
 */
export function getErrorMessage(error: unknown): string {
  const apiError = handleApiError(error);

  if (apiError.errors) {
    return formatValidationErrors(apiError.errors);
  }

  return apiError.message;
}
